import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { websiteApi, Enquiry } from '../api/website.api';

interface EnquiriesPanelProps {
  websiteId: string;
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const EnquiriesPanel: React.FC<EnquiriesPanelProps> = ({ websiteId }) => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const limit = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['enquiries', websiteId, page, fromDate, toDate],
    queryFn: () =>
      websiteApi.getEnquiries(websiteId, page, limit, {
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      }),
    enabled: !!websiteId,
  });

  const markReadMutation = useMutation({
    mutationFn: (enquiryId: string) => websiteApi.markEnquiryRead(websiteId, enquiryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enquiries', websiteId] });
    },
  });

  const handleToggleExpand = (enquiry: Enquiry) => {
    const nextExpanded = expandedId === enquiry._id ? null : enquiry._id;
    setExpandedId(nextExpanded);
    if (nextExpanded && !enquiry.isRead) {
      markReadMutation.mutate(enquiry._id);
    }
  };

  return (
    <div className="nova-card p-4 sm:p-6">
      <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
        <h2 className="text-2xl font-bold text-nova-text">Enquiries</h2>
        {!!data?.unreadCount && (
          <span className="px-3 py-1 text-sm font-semibold rounded-full bg-nova-red/10 text-nova-red">
            {data.unreadCount} unread
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <span className="text-sm font-medium text-nova-text2">Filter by date:</span>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => {
            setFromDate(e.target.value);
            setPage(1);
          }}
          className="px-2 py-1.5 text-sm border border-nova-border rounded-lg"
        />
        <span className="text-nova-text3 text-sm">to</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => {
            setToDate(e.target.value);
            setPage(1);
          }}
          className="px-2 py-1.5 text-sm border border-nova-border rounded-lg"
        />
        {(fromDate || toDate) && (
          <button
            type="button"
            onClick={() => {
              setFromDate('');
              setToDate('');
              setPage(1);
            }}
            className="text-sm text-nova-sky hover:text-nova-sky2 font-medium"
          >
            Clear
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-nova-sky"></div>
        </div>
      ) : !data || data.items.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-nova-text3">
            No enquiries yet. Messages submitted through your website's contact form will show up here.
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {data.items.map((enquiry) => {
              const isExpanded = expandedId === enquiry._id;
              return (
                <div
                  key={enquiry._id}
                  className={`border rounded-lg p-4 transition-colors ${
                    enquiry.isRead ? 'border-nova-border' : 'border-nova-sky/50 bg-nova-sky/5'
                  }`}
                >
                  <button
                    onClick={() => handleToggleExpand(enquiry)}
                    className="w-full flex items-start justify-between text-left gap-4"
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      {!enquiry.isRead && (
                        <span className="mt-1.5 w-2 h-2 rounded-full bg-nova-sky flex-shrink-0" title="Unread" />
                      )}
                      <div className="min-w-0">
                        <p className="font-semibold text-nova-text truncate">{enquiry.name}</p>
                        <p className="text-sm text-nova-text2 truncate">{enquiry.email}</p>
                        {!isExpanded && (
                          <p className="text-sm text-nova-text3 mt-1 truncate">{enquiry.message}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="text-xs text-nova-text3">{formatDate(enquiry.createdAt)}</p>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-nova-border">
                      <p className="text-nova-text whitespace-pre-wrap">{enquiry.message}</p>
                      {enquiry.phone && (
                        <div className="mt-4 flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 sm:gap-4 text-sm">
                          <a href={`tel:${enquiry.phone}`} className="text-nova-sky hover:text-nova-sky2 font-medium">
                            Call {enquiry.phone}
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {data.totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-6">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 border border-nova-border text-nova-text2 rounded-lg hover:bg-nova-bg2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-nova-text3">
                Page {data.page} of {data.totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                disabled={page >= data.totalPages}
                className="px-3 py-1.5 border border-nova-border text-nova-text2 rounded-lg hover:bg-nova-bg2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EnquiriesPanel;
